import { useState, useEffect, useCallback } from 'react';
import { useAdmin } from '@/hooks/useAdmin';
import { toast } from 'sonner';
import { Briefcase, Plus, Trash2, Edit, Loader2, CheckCircle2, XCircle, Search } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = {
  title: '', company: '', location: '', employment_type: 'full_time',
  category: 'ostalo', description: '', salary_range_eur: '',
  application_email: '', application_url: '', expires_at: '',
};

export default function AdminJobs() {
  const { authFetch } = useAdmin();
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 50 });
      if (status) params.set('status', status);
      const r = await authFetch(`${API}/admin/jobs?${params}`);
      const d = await r.json();
      setJobs(d.jobs || []);
      setTotal(d.total || 0);
    } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  }, [authFetch, page, status]);

  useEffect(() => { load(); }, [load]);

  async function submit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const url = editingId ? `${API}/admin/jobs/${editingId}` : `${API}/admin/jobs`;
      const method = editingId ? 'PUT' : 'POST';
      const r = await authFetch(url, { method, body: JSON.stringify(form) });
      if (!r.ok) throw new Error((await r.json()).detail || 'Greška');
      toast.success(editingId ? 'Oglas ažuriran' : 'Oglas kreiran');
      setForm(EMPTY); setEditingId(null); setShowForm(false);
      load();
    } catch (err) { toast.error(err.message); } finally { setSaving(false); }
  }

  async function remove(id) {
    if (!window.confirm('Obrisati ovaj oglas?')) return;
    await authFetch(`${API}/admin/jobs/${id}`, { method: 'DELETE' });
    load();
  }

  async function approve(id) { await authFetch(`${API}/admin/jobs/${id}/approve`, { method: 'POST' }); load(); }
  async function reject(id) { await authFetch(`${API}/admin/jobs/${id}/reject`, { method: 'POST' }); load(); }

  function startEdit(j) {
    setEditingId(j.id);
    setForm({ ...EMPTY, ...j, expires_at: j.expires_at?.slice(0, 10) || '' });
    setShowForm(true);
  }

  return (
    <div className="p-6 max-w-6xl" data-testid="admin-jobs-page">
      <header className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-serif text-3xl text-zinc-100 flex items-center gap-3"><Briefcase size={28} className="text-red-500" /> E-mina Posao — oglasi</h1>
          <p className="text-xs text-zinc-400 mt-1">Upravljajte oglasima za posao i ručnim odobrenjem poslanih prijava.</p>
        </div>
        <button onClick={() => { setEditingId(null); setForm(EMPTY); setShowForm(s => !s); }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 rounded-md text-sm font-medium"
                data-testid="new-job-btn">
          <Plus size={14} /> {showForm ? 'Zatvori' : 'Novi oglas'}
        </button>
      </header>

      {showForm && (
        <form onSubmit={submit} className="mb-6 p-5 rounded-xl border border-zinc-800 bg-zinc-900/50 space-y-4" data-testid="job-form">
          <div className="grid md:grid-cols-2 gap-4">
            <input required placeholder="Naziv radnog mjesta" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
                   data-testid="job-title" className="bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-sm" />
            <input required placeholder="Tvrtka" value={form.company} onChange={e => setForm({ ...form, company: e.target.value })}
                   data-testid="job-company" className="bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-sm" />
            <input placeholder="Lokacija" value={form.location} onChange={e => setForm({ ...form, location: e.target.value })}
                   className="bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-sm" />
            <select value={form.employment_type} onChange={e => setForm({ ...form, employment_type: e.target.value })}
                    className="bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-sm">
              <option value="full_time">Puno radno vrijeme</option>
              <option value="part_time">Pola radnog vremena</option>
              <option value="contract">Ugovor / projekt</option>
              <option value="internship">Praksa</option>
              <option value="remote">Na daljinu</option>
            </select>
            <input placeholder="Raspon plaće (npr. 1500-2000 EUR)" value={form.salary_range_eur} onChange={e => setForm({ ...form, salary_range_eur: e.target.value })}
                   className="bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-sm" />
            <input type="date" value={form.expires_at} onChange={e => setForm({ ...form, expires_at: e.target.value })}
                   className="bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-sm" />
            <input type="email" placeholder="Email za prijave" value={form.application_email} onChange={e => setForm({ ...form, application_email: e.target.value })}
                   className="bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-sm" />
            <input placeholder="URL za prijave (opcionalno)" value={form.application_url} onChange={e => setForm({ ...form, application_url: e.target.value })}
                   className="bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-sm" />
          </div>
          <textarea placeholder="Opis posla" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                    rows={6} className="w-full bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-sm" data-testid="job-desc" />
          <button type="submit" disabled={saving}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-red-600 hover:bg-red-700 rounded-md text-white font-medium disabled:opacity-50"
                  data-testid="job-submit">
            {saving ? <Loader2 size={14} className="animate-spin" /> : editingId ? <Edit size={14} /> : <Plus size={14} />} {editingId ? 'Spremi promjene' : 'Kreiraj oglas'}
          </button>
        </form>
      )}

      <div className="flex items-center gap-3 mb-3">
        <select value={status} onChange={e => { setStatus(e.target.value); setPage(1); }}
                data-testid="job-status-filter"
                className="bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm">
          <option value="">Svi statusi</option>
          <option value="active">Aktivno</option>
          <option value="pending">Na odobrenju</option>
          <option value="rejected">Odbijeno</option>
        </select>
        <span className="text-xs text-zinc-500 ml-auto">Ukupno: {total}</span>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="animate-spin text-zinc-500" /></div>
      ) : jobs.length === 0 ? (
        <div className="text-center py-12 text-zinc-500">Nema oglasa</div>
      ) : (
        <div className="bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-800 text-zinc-500 text-left">
                <th className="px-4 py-3 font-medium">Naziv</th>
                <th className="px-4 py-3 font-medium">Tvrtka</th>
                <th className="px-4 py-3 font-medium">Lokacija</th>
                <th className="px-4 py-3 font-medium">Tip</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium w-32">Akcije</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map(j => (
                <tr key={j.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30" data-testid={`job-row-${j.id}`}>
                  <td className="px-4 py-3 text-zinc-200">{j.title}</td>
                  <td className="px-4 py-3 text-zinc-400">{j.company}</td>
                  <td className="px-4 py-3 text-zinc-400">{j.location || '—'}</td>
                  <td className="px-4 py-3 text-zinc-500 text-xs">{j.employment_type}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-1.5 py-0.5 rounded ${j.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : j.status === 'pending' ? 'bg-amber-500/20 text-amber-400' : 'bg-red-500/20 text-red-400'}`}>{j.status}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      {j.status === 'pending' && (
                        <>
                          <button onClick={() => approve(j.id)} className="p-1.5 text-zinc-500 hover:text-emerald-400" title="Odobri" data-testid={`job-approve-${j.id}`}><CheckCircle2 size={14} /></button>
                          <button onClick={() => reject(j.id)} className="p-1.5 text-zinc-500 hover:text-red-400" title="Odbij" data-testid={`job-reject-${j.id}`}><XCircle size={14} /></button>
                        </>
                      )}
                      <button onClick={() => startEdit(j)} className="p-1.5 text-zinc-500 hover:text-blue-400" data-testid={`job-edit-${j.id}`}><Edit size={14} /></button>
                      <button onClick={() => remove(j.id)} className="p-1.5 text-zinc-500 hover:text-red-400" data-testid={`job-delete-${j.id}`}><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
